import React, { useEffect } from 'react';
import { useSubscription, PLANS, FEATURE_TIER } from '../contexts/SubscriptionContext';

const FEATURE_LABELS = {
  voicemail : 'Voicemail',
  sms       : 'SMS Messaging',
  video     : 'Video Rooms',
  api       : 'API Access',
  numbers   : 'Phone Numbers',
  softphone : 'Softphone',
};

export default function UpgradeModal({ feature, theme, onClose }) {
  const { upgrade } = useSubscription();
  const isDark = theme === 'dark';

  const requiredTierId = FEATURE_TIER[feature] || 'pro';
  const plan = PLANS.find((p) => p.id === requiredTierId) || PLANS[1];
  const featureLabel = FEATURE_LABELS[feature] || feature;

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleUpgrade = async () => {
    try {
      await upgrade(plan.id);
      onClose();
    } catch (err) {
      console.error('Upgrade failed:', err);
    }
  };

  const overlay = {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.7)',
    backdropFilter: 'blur(4px)',
    zIndex: 9999,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '24px',
  };

  const modal = {
    background: isDark ? '#0d0d1a' : '#ffffff',
    border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
    borderRadius: '16px',
    padding: '36px',
    maxWidth: '440px',
    width: '100%',
    boxShadow: '0 25px 60px rgba(0,0,0,0.5)',
    position: 'relative',
  };

  const accent = plan.color || '#3b82f6';

  const btnPrimary = {
    width: '100%',
    padding: '14px',
    borderRadius: '10px',
    border: 'none',
    background: accent,
    color: '#fff',
    fontWeight: 700,
    fontSize: '15px',
    cursor: 'pointer',
    marginTop: '24px',
  };

  const btnSecondary = {
    width: '100%',
    padding: '12px',
    borderRadius: '10px',
    border: isDark ? '1px solid rgba(255,255,255,0.15)' : '1px solid #e2e8f0',
    background: 'transparent',
    color: isDark ? '#8888aa' : '#64748b',
    fontWeight: 500,
    fontSize: '14px',
    cursor: 'pointer',
    marginTop: '10px',
  };

  const closeBtn = {
    position: 'absolute', top: '16px', right: '16px',
    background: 'transparent', border: 'none',
    color: isDark ? '#8888aa' : '#94a3b8',
    cursor: 'pointer', fontSize: '20px', lineHeight: 1,
    padding: '4px',
  };

  return (
    <div style={overlay} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={modal}>
        <button style={closeBtn} onClick={onClose} aria-label="Close">x</button>

        {/* Badge */}
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: '6px',
          background: `${accent}22`, border: `1px solid ${accent}44`,
          borderRadius: '20px', padding: '4px 12px',
          color: accent, fontSize: '12px', fontWeight: 700,
          marginBottom: '20px', letterSpacing: '0.05em', textTransform: 'uppercase',
        }}>
          {plan.name} Feature
        </div>

        {/* Heading */}
        <h2 style={{
          color: isDark ? '#fff' : '#1e293b',
          fontSize: '22px', fontWeight: 800,
          margin: '0 0 8px',
        }}>
          Unlock {featureLabel}
        </h2>
        <p style={{
          color: isDark ? '#8888aa' : '#64748b',
          fontSize: '14px', margin: '0 0 20px', lineHeight: 1.6,
        }}>
          {featureLabel} is available on the <strong style={{ color: accent }}>{plan.name}</strong> plan.
          {plan.price ? ` Just ${plan.label} — cancel anytime.` : ''}
        </p>

        {/* Feature list */}
        <div style={{
          background: isDark ? 'rgba(255,255,255,0.04)' : '#f8fafc',
          borderRadius: '10px', padding: '16px',
          border: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid #e2e8f0',
        }}>
          <div style={{
            color: isDark ? '#aaa' : '#64748b',
            fontSize: '11px', fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: '0.08em',
            marginBottom: '10px',
          }}>
            Included in {plan.name}
          </div>
          {plan.features.map((f, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'flex-start', gap: '8px',
              color: isDark ? '#ccc' : '#374151',
              fontSize: '13px', padding: '4px 0',
            }}>
              <span style={{ color: accent, flexShrink: 0, marginTop: '1px' }}>+</span>
              {f}
            </div>
          ))}
        </div>

        {/* Price */}
        {plan.price && (
          <div style={{
            textAlign: 'center', marginTop: '20px',
            color: isDark ? '#8888aa' : '#64748b', fontSize: '13px',
          }}>
            <span style={{ fontSize: '28px', fontWeight: 800, color: isDark ? '#fff' : '#1e293b' }}>
              ${plan.price}
            </span>
            {' '}/month per user
          </div>
        )}

        <button style={btnPrimary} onClick={handleUpgrade}>
          Upgrade to {plan.name} {plan.price ? `- ${plan.label}` : ''}
        </button>
        <button style={btnSecondary} onClick={onClose}>
          Maybe later
        </button>
      </div>
    </div>
  );
}
